'use client'

import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Button, Cell, Spacer, Text } from '@vinted/web-ui'
import { camelCase } from 'lodash'

import { PepDetails } from '@marketplace-web/domain/politically-exposed-person'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { isValueInObject } from '@marketplace-web/shared/utils'
import { AddressModel, AddressType } from '@marketplace-web/address/address-data'
import { clickEvent } from '_libs/common/event-tracker/events'
import { Screen } from 'constants/tracking/screens'
import { ClickableElement } from 'constants/tracking/clickable-elements'

import GeneralDetails from '../../BalanceActivationForm/SetupForm/GeneralDetails'
import { Field, FormModel } from '../../BalanceActivationForm/types'
import BillingAddressDetails from '../../BalanceActivationForm/SetupForm/BillingAddressDetails'

type Props = {
  defaultValues: FormModel
  isSsnNumberRequired: boolean
  isNationalityRequired: boolean
  personalIdNumberRequiredNationalities?: Array<string>
  isBalanceActivated: boolean
  bannedNationalities: Array<string>
  isPepDeclarationRequired: boolean
  lockedFields: Array<string>
  maskedFields: Array<string>
  agreementHint: string
  apiErrors: Array<ErrorItem>
  isLoading: boolean
  wide?: boolean
  addressType?: AddressType
  address: AddressModel | null
  setAddress: (address: AddressModel | null) => void
  onAddressClick: (form: FormModel) => void
  onSubmit: (form: FormModel) => void
  onCancel: () => void
}

const SetupForm = ({
  defaultValues,
  isSsnNumberRequired,
  isNationalityRequired,
  personalIdNumberRequiredNationalities,
  isBalanceActivated,
  isPepDeclarationRequired,
  bannedNationalities,
  lockedFields,
  maskedFields,
  agreementHint,
  apiErrors,
  isLoading,
  wide,
  address,
  addressType,
  setAddress,
  onAddressClick,
  onSubmit,
  onCancel,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('wallet.activation')

  const formMethods = useForm<FormModel>({ defaultValues })

  useEffect(() => {
    apiErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field) as Field

      if (!isValueInObject(fieldName, Field) || fieldName === Field.FullName) return

      formMethods.setError(fieldName, { type: 'manual', message: value })
    })
  }, [apiErrors, formMethods])

  function handleAddressClick() {
    const values = formMethods.getValues()

    return onAddressClick(values)
  }

  useEffect(() => {
    if (!formMethods.formState.submitCount || !Object.keys(formMethods.formState.errors).length) {
      return
    }

    track(
      clickEvent({
        target: ClickableElement.Submit,
        screen: Screen.PaymentsAccountInTransaction,
        targetDetails: 'validation_failed',
      }),
    )
  }, [formMethods.formState.submitCount, formMethods.formState.errors, track])

  function handleFormSubmit() {
    const values = formMethods.getValues()

    return onSubmit(values)
  }

  function renderGeneralDetails() {
    return (
      <GeneralDetails
        isLoading={isLoading}
        wide={wide}
        bannedNationalities={bannedNationalities}
        personalIdNumberRequiredNationalities={personalIdNumberRequiredNationalities}
        isSsnNumberRequired={isSsnNumberRequired}
        isNationalityRequired={isNationalityRequired}
        isBalanceActivated={isBalanceActivated}
        lockedFields={lockedFields}
        maskedFields={maskedFields}
      />
    )
  }

  const renderBillingAddress = () => {
    return (
      <BillingAddressDetails
        externalModal
        address={address}
        isDisabled={isLoading}
        addressType={addressType}
        setAddress={setAddress}
        onClick={handleAddressClick}
      />
    )
  }

  const renderPepDetails = () => {
    if (!isPepDeclarationRequired) return null

    return <PepDetails screenName={Screen.PaymentsAccountInTransaction} isLoading={isLoading} />
  }

  const renderFooter = () => {
    const submitButtonText = isBalanceActivated
      ? translate('actions.update')
      : translate('actions.save')

    return (
      <Cell styling={Cell.Styling.Wide}>
        <Text as="h4" html type={Text.Type.Caption} text={agreementHint} />
        <Spacer size={Spacer.Size.Large} />
        <Button
          type={Button.Type.Submit}
          styling={Button.Styling.Filled}
          disabled={isLoading}
          isLoading={isLoading}
          testId="balance-activation-modal--setup-form--submit-button"
          onClick={(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            if (isLoading) {
              event.preventDefault()
            }
          }}
        >
          {submitButtonText}
        </Button>
        <Spacer size={Spacer.Size.Large} />
        <Button styling={Button.Styling.Flat} disabled={isLoading} onClick={onCancel}>
          {translate('actions.cancel')}
        </Button>
      </Cell>
    )
  }

  return (
    <form onSubmit={formMethods.handleSubmit(handleFormSubmit)} noValidate>
      <FormProvider {...formMethods}>
        {renderGeneralDetails()}
        {renderBillingAddress()}
        {renderPepDetails()}
        {renderFooter()}
      </FormProvider>
    </form>
  )
}

export default SetupForm
